/**
 * Parse data attributes from section and build Swiper options
 */
import { defaultOptions } from './default-options.js';

/**
 * Parse basic numeric and boolean options
 */
function parseBasicOptions(data, options) {
  // Slides per view
  if (data.slidesPerView) {
    const slidesPerViewValue = parseFloat(data.slidesPerView);
    options.slidesPerView = isNaN(slidesPerViewValue) ? 'auto' : slidesPerViewValue;
  }

  // Space between slides
  if (data.spaceBetween) {
    options.spaceBetween = parseInt(data.spaceBetween);
  }

  // Animation speed
  if (data.speed) {
    options.speed = parseInt(data.speed);
  }

  // Centered slides
  if (data.centeredSlides) {
    options.centeredSlides = data.centeredSlides === 'true';
  }

  // Loop mode
  if (data.loop) {
    options.loop = data.loop === 'true';
  }

  // Looped slides count
  if (data.loopedSlides) {
    options.loopedSlides = parseInt(data.loopedSlides);
  }

  // Watch slides progress
  if (data.watchSlidesProgress) {
    options.watchSlidesProgress = data.watchSlidesProgress === 'true';
  }

  // Center insufficient slides
  if (data.centerInsufficientSlides) {
    options.centerInsufficientSlides = data.centerInsufficientSlides === 'true';
  }

  // Long swipes
  if (data.longSwipes) {
    options.longSwipes = data.longSwipes === 'true';
  }

  // Rewind
  if (data.rewind === 'true') {
    options.rewind = true;
  }
}

/**
 * Parse pagination options
 */
function parsePagination(section, data, options) {
  if (data.pagination === 'false') {
    options.pagination = false;
    return;
  }

  let paginationContainer = null;

  // For nested carousels, check if there's a custom pagination ID
  if (data.nestedCarousel === 'true' && data.paginationId) {
    paginationContainer = document.getElementById(data.paginationId);
  } else {
    // Look for custom pagination container within section
    paginationContainer = section.querySelector('[class*="__pagination"]');
  }

  if (paginationContainer) {
    options.pagination = {
      el: paginationContainer,
      clickable: true,
      type: data.paginationType || 'bullets'
    };
  } else {
    // Fallback to default pagination
    options.pagination = {
      el: '.swiper-pagination',
      clickable: true,
      type: data.paginationType || 'bullets'
    };
  }
}

/**
 * Parse navigation options
 */
function parseNavigation(section, data, options) {
  if (data.navigation === 'false') {
    options.navigation = false;
    return;
  }

  // Look for custom navigation buttons in the section
  const prevButton = section.querySelector('[class*="__button--prev"]');
  const nextButton = section.querySelector('[class*="__button--next"]');

  if (prevButton && nextButton) {
    options.navigation = {
      prevEl: prevButton,
      nextEl: nextButton,
    };
  } else {
    // Fallback to default navigation
    options.navigation = {
      prevEl: '.swiper-button-prev',
      nextEl: '.swiper-button-next',
    };
  }
}

/**
 * Parse scrollbar options
 */
function parseScrollbar(data, options) {
  if (data.scrollbar === 'true') {
    options.scrollbar = {
      el: '.swiper-scrollbar',
      draggable: true
    };
  }
}

/**
 * Parse Swiper observer options.
 *
 * `data-observer="true"`               → enables Swiper's `observer` + `observeParents`,
 *                                         which watches the swiper root and its ancestors
 *                                         for DOM mutations and re-runs `update()` so
 *                                         swipers built around dynamically-changing slide
 *                                         lists (e.g. <bundle-gallery> swapping media on
 *                                         variant change) keep working without manual reinit.
 *
 * `data-observe-slide-children="true"` → enables Swiper's `observeSlideChildren`, which
 *                                         extends the mutation observer to watch the
 *                                         children of every `.swiper-slide` element. Per
 *                                         Swiper docs this option is a no-op unless
 *                                         `observer` is also enabled, so we silently
 *                                         require `data-observer="true"` here too.
 *
 * `data-resize-observer="true"`        → enables Swiper's `resizeObserver`, which uses
 *                                         ResizeObserver instead of the window resize
 *                                         event to detect container size changes. Useful
 *                                         for swipers inside collapsible/animated parents.
 *
 * All three attrs are forwarded verbatim to Swiper's options object; they're only set
 * when explicitly `"true"` to keep options sparse for the common case.
 */
function parseObservers(data, options) {
  if (data.observer === 'true') {
    options.observer = true;
    options.observeParents = true;

    // observeSlideChildren is gated on observer === true because it's a no-op
    // without the parent observer enabled; setting it alone would silently
    // mismatch Swiper's actual behavior.
    if (data.observeSlideChildren === 'true') {
      options.observeSlideChildren = true;
    }
  }

  if (data.resizeObserver === 'true') {
    options.resizeObserver = true;
  }
}

/**
 * Parse breakpoints configuration
 */
function parseBreakpoints(data, options) {
  if (!data.breakpoints) {
    return;
  }

  try {
    const breakpoints = JSON.parse(data.breakpoints);

    // Process each breakpoint
    Object.keys(breakpoints).forEach(breakpoint => {
      const breakpointOptions = breakpoints[breakpoint];

      // Handle slidesPerView - keep 'auto' as string, convert numbers
      if (breakpointOptions.slidesPerView !== 'auto') {
        const slidesPerView = parseFloat(breakpointOptions.slidesPerView);
        breakpointOptions.slidesPerView = isNaN(slidesPerView) 
          ? breakpointOptions.slidesPerView 
          : slidesPerView;
      }

      // Add fixLoop handlers for mobile breakpoints if enabled
      if ((breakpoint === '0' || breakpoint === '375') && data.fixLoop === 'true') {
        breakpointOptions.on = {
          init: function() {
            if (this.params.loop) {
              this.fixLoop();
            }
          },
          slideChange: function() {
            if (this.params.loop) {
              this.fixLoop();
            }
          }
        };
      }
    });

    options.breakpoints = breakpoints;
  } catch (e) {
    console.error('Error parsing breakpoints:', e);
  }
}

/**
 * Main parser function - combines all option parsing
 * @param {HTMLElement} section - The section element with data attributes
 * @param {Object} baseOptions - Base options to extend from
 * @returns {Object} Complete Swiper options object
 */
export function parseOptionsFromAttributes(section, baseOptions = defaultOptions) {
  const options = { ...baseOptions };
  const data = section.dataset;

  parseBasicOptions(data, options);
  parsePagination(section, data, options);
  parseNavigation(section, data, options);
  parseScrollbar(data, options);
  parseObservers(data, options);
  parseBreakpoints(data, options);

  // Autoplay and vertical-sync are handled by their respective feature plugins
  // (autoplay.js, synced-swipers.js) so cross-cutting behaviors stay together.

  return options;
}

